import { toTrimmedNonEmptyString } from '@/lib/utils/type-guards';
import type { TimeRange } from '@/types/timeline-analysis';

export const TIMELINE_ANALYSIS_KEY = 'timeline-analysis';
export const TIMELINE_ANALYSIS_INFO_KEY = 'timeline-analysis-info';
export const TIMELINE_ANALYSIS_SETTINGS_KEY = 'timeline-analysis-settings';
export const TIMELINE_INSIGHTS_KEY = 'timeline-insights';
export const TIMELINE_INSIGHTS_SUMMARY_KEY = 'timeline-insights-summary';
export const TIMELINE_LINK_INSIGHTS_KEY = 'timeline-link-insights';
export const TIMELINE_VIDEO_INSIGHTS_KEY = 'timeline-video-insights';
export const TIMELINE_INSIGHTS_HISTORY_KEY = 'timeline-insights-history';

export function timelineAnalysisKey(timeRange: TimeRange) {
  return [TIMELINE_ANALYSIS_KEY, timeRange] as const;
}

export function timelineAnalysisInfoKey(timeRange: TimeRange) {
  return [TIMELINE_ANALYSIS_INFO_KEY, timeRange] as const;
}

export function timelineAnalysisSettingsKey() {
  return [TIMELINE_ANALYSIS_SETTINGS_KEY] as const;
}

export function timelineInsightsKey(timeRange: TimeRange) {
  return [TIMELINE_INSIGHTS_KEY, timeRange] as const;
}

export type TimelineInsightsSummaryKey =
  | readonly [typeof TIMELINE_INSIGHTS_SUMMARY_KEY, TimeRange]
  | readonly [typeof TIMELINE_INSIGHTS_SUMMARY_KEY, TimeRange, string];

export function timelineInsightsSummaryKey(timeRange: TimeRange, timezone?: string | null): TimelineInsightsSummaryKey {
  const normalizedTimezone = toTrimmedNonEmptyString(timezone);
  if (normalizedTimezone) {
    return [TIMELINE_INSIGHTS_SUMMARY_KEY, timeRange, normalizedTimezone] as const;
  }
  return [TIMELINE_INSIGHTS_SUMMARY_KEY, timeRange] as const;
}

export function timelineLinkInsightsKey(timeRange: TimeRange) {
  return [TIMELINE_LINK_INSIGHTS_KEY, timeRange] as const;
}

export function timelineVideoInsightsKey(timeRange: TimeRange) {
  return [TIMELINE_VIDEO_INSIGHTS_KEY, timeRange] as const;
}

export interface TimelineInsightsListKeyOptions {
  limit: number;
  sortDirection: string;
  sortField: string;
  timeRange: TimeRange;
  timezone?: string | null;
}

export function timelineInsightsListKey({
  timeRange,
  limit,
  sortField,
  sortDirection,
  timezone,
}: TimelineInsightsListKeyOptions) {
  const normalizedTimezone = toTrimmedNonEmptyString(timezone) ?? 'UTC';
  return [TIMELINE_INSIGHTS_KEY, timeRange, limit, sortField, sortDirection, normalizedTimezone] as const;
}

export type TimelineInsightsHistoryKey = readonly [typeof TIMELINE_INSIGHTS_HISTORY_KEY];

export function timelineInsightsHistoryKey(): TimelineInsightsHistoryKey {
  return [TIMELINE_INSIGHTS_HISTORY_KEY] as const;
}

export function isTimelineAnalysisInfoKey(
  queryKey: readonly unknown[]
): queryKey is ReturnType<typeof timelineAnalysisInfoKey> {
  return Array.isArray(queryKey) && queryKey[0] === TIMELINE_ANALYSIS_INFO_KEY;
}
